import { useEffect, useRef, useState } from "react";

const FRAME = 280;
const OUTPUT = 1024;
const MIN_ZOOM = 1;
const MAX_ZOOM = 6;

interface Props {
  file: File;
  onCancel: () => void;
  onConfirm: (cropped: Blob) => void;
  saving?: boolean;
}

export function AvatarCropper({ file, onCancel, onConfirm, saving }: Props) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const s = useRef({ zoom: 1, ox: 0, oy: 0 });
  const [, redraw] = useState(0);

  const gesture = useRef<
    | { type: "pan"; startX: number; startY: number; startOx: number; startOy: number }
    | { type: "pinch"; startDist: number; startZoom: number; startOx: number; startOy: number; midX: number; midY: number }
    | null
  >(null);

  const mouseDown = useRef<{ startX: number; startY: number; startOx: number; startOy: number } | null>(null);

  // Keep a stable ref to applyOffset so the once-registered touch listeners
  // always invoke the latest version (avoids stale-closure drift).
  const applyRef = useRef<(ox: number, oy: number, zoom?: number) => void>(null!);
  applyRef.current = (ox: number, oy: number, zoom = s.current.zoom) => {
    const img = imgRef.current;
    if (!img) return;
    const base = FRAME / Math.min(img.naturalWidth, img.naturalHeight);
    const dw = img.naturalWidth * base * zoom;
    const dh = img.naturalHeight * base * zoom;
    const maxX = Math.max(0, (dw - FRAME) / 2);
    const maxY = Math.max(0, (dh - FRAME) / 2);
    s.current = {
      zoom,
      ox: Math.max(-maxX, Math.min(maxX, ox)),
      oy: Math.max(-maxY, Math.min(maxY, oy)),
    };
    redraw((n) => n + 1);
  };

  // Load image from file.
  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      s.current = { zoom: 1, ox: 0, oy: 0 };
      redraw((n) => n + 1);
    };
    img.onerror = () => onCancel();
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  // Non-passive touch listeners so preventDefault() works on iOS Safari.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();

      if (e.touches.length === 1) {
        gesture.current = {
          type: "pan",
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startOx: s.current.ox,
          startOy: s.current.oy,
        };
      } else if (e.touches.length === 2) {
        const t0 = e.touches[0], t1 = e.touches[1];
        const midClientX = (t0.clientX + t1.clientX) / 2;
        const midClientY = (t0.clientY + t1.clientY) / 2;
        gesture.current = {
          type: "pinch",
          startDist: Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY),
          startZoom: s.current.zoom,
          startOx: s.current.ox,
          startOy: s.current.oy,
          // Midpoint relative to container center (same coordinate space as ox/oy).
          midX: midClientX - rect.left - FRAME / 2,
          midY: midClientY - rect.top - FRAME / 2,
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const g = gesture.current;
      if (!g) return;

      if (g.type === "pan" && e.touches.length >= 1) {
        const dx = e.touches[0].clientX - g.startX;
        const dy = e.touches[0].clientY - g.startY;
        applyRef.current(g.startOx + dx, g.startOy + dy);
      } else if (g.type === "pinch" && e.touches.length >= 2) {
        const t0 = e.touches[0], t1 = e.touches[1];
        const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
        const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, g.startZoom * (dist / g.startDist)));
        const ratio = newZoom / g.startZoom;
        // Scale the offset around the pinch midpoint so that point stays fixed.
        applyRef.current(
          g.midX + (g.startOx - g.midX) * ratio,
          g.midY + (g.startOy - g.midY) * ratio,
          newZoom,
        );
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        gesture.current = null;
      } else if (e.touches.length === 1 && gesture.current?.type === "pinch") {
        // One finger lifted — transition pinch → pan from current position.
        gesture.current = {
          type: "pan",
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startOx: s.current.ox,
          startOy: s.current.oy,
        };
      }
    };

    const opts = { passive: false } as AddEventListenerOptions;
    el.addEventListener("touchstart", onTouchStart, opts);
    el.addEventListener("touchmove", onTouchMove, opts);
    el.addEventListener("touchend", onTouchEnd, opts);
    el.addEventListener("touchcancel", onTouchEnd, opts);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mouse wheel zoom for desktop.
  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    applyRef.current(s.current.ox, s.current.oy, Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, s.current.zoom - e.deltaY * 0.002)));
  };

  // Mouse drag for desktop.
  const onMouseDown = (e: React.MouseEvent) => {
    mouseDown.current = { startX: e.clientX, startY: e.clientY, startOx: s.current.ox, startOy: s.current.oy };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!mouseDown.current) return;
    applyRef.current(mouseDown.current.startOx + e.clientX - mouseDown.current.startX, mouseDown.current.startOy + e.clientY - mouseDown.current.startY);
  };
  const onMouseUp = () => { mouseDown.current = null; };

  // Canvas export: read the visible square from the source image.
  const handleSave = async () => {
    const img = imgRef.current;
    if (!img) return;
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const base = FRAME / Math.min(img.naturalWidth, img.naturalHeight);
    const scale = base * s.current.zoom;
    const cx = img.naturalWidth / 2 - s.current.ox / scale;
    const cy = img.naturalHeight / 2 - s.current.oy / scale;
    const sw = FRAME / scale;

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    ctx.drawImage(img, cx - sw / 2, cy - sw / 2, sw, sw, 0, 0, OUTPUT, OUTPUT);

    let blob: Blob | null = null;
    if (canvas.toBlob) {
      blob = await new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.92));
    } else {
      const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
      const arr = dataUrl.split(",");
      const bstr = atob(arr[1]);
      const u8 = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
      blob = new Blob([u8], { type: "image/jpeg" });
    }
    if (blob) onConfirm(blob);
  };

  const img = imgRef.current;
  const { zoom, ox, oy } = s.current;
  const base = img ? FRAME / Math.min(img.naturalWidth, img.naturalHeight) : 1;
  const dw = img ? img.naturalWidth * base * zoom : 0;
  const dh = img ? img.naturalHeight * base * zoom : 0;

  return (
    <div
      role="dialog"
      aria-label="Crop profile picture"
      style={{
        position: "fixed", inset: 0, zIndex: 80,
        background: "rgba(20, 18, 14, 0.78)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
    >
      <div style={{
        width: "100%", maxWidth: 360,
        background: "hsl(var(--cream))",
        border: "1px solid hsl(var(--ink))",
        padding: 20,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 18,
      }}>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 20,
          color: "hsl(var(--ink))", alignSelf: "flex-start",
        }}>
          Position your photo
        </div>

        <div
          ref={containerRef}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          style={{
            position: "relative",
            width: FRAME, height: FRAME,
            overflow: "hidden",
            background: "hsl(var(--ink))",
            border: "2px solid hsl(var(--ink))",
            borderRadius: "50%",
            touchAction: "none",
            cursor: mouseDown.current ? "grabbing" : "grab",
            userSelect: "none",
            flexShrink: 0,
          }}
        >
          {img && (
            <img
              src={img.src}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                width: dw, height: dh,
                // Tailwind preflight sets max-width:100% and height:auto on
                // all <img> elements. Override so the image can render wider
                // or taller than the container (landscape/zoomed photos).
                maxWidth: "none",
                maxHeight: "none",
                left: FRAME / 2 - dw / 2 + ox,
                top: FRAME / 2 - dh / 2 + oy,
                pointerEvents: "none",
                userSelect: "none",
              }}
            />
          )}
        </div>

        <div className="caps" style={{ fontSize: 9, color: "hsl(var(--ink-2))", textAlign: "center" }}>
          Drag to reposition · pinch to zoom
        </div>

        <div style={{ display: "flex", gap: 8, width: "100%" }}>
          <button type="button" className="btn" style={{ flex: 1, padding: 12 }} onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" style={{ flex: 2, padding: 12 }} onClick={handleSave} disabled={saving || !img}>
            {saving ? "Saving…" : "Use this photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
